import { Illustration } from '@eventbrite/eds-illustration';
import {
    addFollow,
    FollowCard,
    withFollow,
} from '@eventbrite/follow-organizer-ebui';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { useEnvContext } from '../../../context';
import { Organizer } from '../utils';
import { ORGANIZER_IMAGE_DIAMETER } from './constants';
import './OrganizerCard.scss';
import { getRandomSuperCrop } from './utils';

interface FollowOrganizerWidgetProps {
    organizers: Organizer[];
    isAuthenticated: boolean;
}

const EnhancedFollowButton = withFollow(FollowCard);

const FollowOrganizerWidget: React.FunctionComponent<
    FollowOrganizerWidgetProps
> = ({ organizers }) => {
    return (
        <EnhancedFollowButton
            style="solitary"
            size="standard"
            isOnlyButton={true}
            organizerId={organizers[0]}
        />
    );
};

const EnhancedFollowOrganizerWidget = addFollow(FollowOrganizerWidget);

export interface OrganizerProfileImageProps {
    imageUrl: string | null;
    organizerName: string;
}

const OrganizerProfileImage: React.FunctionComponent<
    OrganizerProfileImageProps
> = ({ imageUrl, organizerName }) => {
    return (
        <div
            className="ttd-organizer-card__image-container"
            data-testid="ttd-organizer-card-image-container"
        >
            {!imageUrl ? (
                <Illustration
                    type={getRandomSuperCrop()}
                    width={ORGANIZER_IMAGE_DIAMETER}
                    height={ORGANIZER_IMAGE_DIAMETER}
                />
            ) : (
                <img
                    className="ttd-organizer-card__image"
                    src={imageUrl}
                    width={ORGANIZER_IMAGE_DIAMETER}
                    height={ORGANIZER_IMAGE_DIAMETER}
                    alt={gettext("%(organizerName)s's Logo", {
                        organizerName,
                    }).toString()}
                    data-testid="ttd-organizer-card-image"
                />
            )}
        </div>
    );
};

export interface OrganizerCardProps {
    id: string;
    imageUrl: string | null;
    organizerName: string;
    subtitle: string;
    organizerUrl: string;
    isFollowing: boolean;
}

const OrganizerCard: React.FunctionComponent<OrganizerCardProps> = ({
    id,
    imageUrl,
    isFollowing,
    organizerName,
    subtitle,
    organizerUrl,
}) => {
    const { user } = useEnvContext();

    return (
        <section
            className="ttd-organizer-card eds-bg-color--background"
            data-testid="ttd-organizer-card"
        >
            <div className="ttd-organizer-card__image-and-text">
                <a
                    href={organizerUrl}
                    target="_blank"
                    className="ttd-organizer-card__link"
                    rel="noopener noreferrer"
                    aria-label={gettext('See more of %(organizerName)s', {
                        organizerName,
                    }).toString()}
                    data-testid="ttd-organizer-card-image-link"
                >
                    <OrganizerProfileImage
                        imageUrl={imageUrl}
                        organizerName={organizerName}
                    />
                </a>

                <a
                    href={organizerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ttd-organizer-card__link"
                    aria-label={gettext('See more of %(organizerName)s', {
                        organizerName,
                    }).toString()}
                    data-testid="ttd-organizer-card-text-link"
                >
                    <div className="ttd-organizer-card__title-container">
                        <h4
                            className="ttd-organizer-card__title eds-text-bl eds-text-color--ui-800"
                            data-testid="ttd-organizer-card-title"
                        >
                            {organizerName}
                        </h4>
                    </div>
                    <div className="ttd-organizer-card__subtitle-container">
                        <p
                            className="ttd-organizer-card__subtitle eds-text-bm eds-text-color--ui-600"
                            data-testid="ttd-organizer-card-subtitle"
                        >
                            {subtitle}
                        </p>
                    </div>
                </a>
            </div>
            <div className="ttd-organizer-card__cta">
                <EnhancedFollowOrganizerWidget
                    isAuthenticated={user?.isAuthenticated || false}
                    organizers={[id]}
                    isFollowing={isFollowing}
                    gaCategory={''}
                    gaSettings={{}}
                />
            </div>
        </section>
    );
};

export default OrganizerCard;
